"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

exports.createInterceptor = createInterceptor;

var _axios = require("axios");

var _axios2 = _interopRequireDefault(_axios);

function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    default: obj
  };
}

function defaultConfigWithAccess(config, accessToken) {
  return _extends({}, config, {
    headers: _extends({}, config.headers, {
      Authorization: "Bearer " + accessToken
    })
  });
}

function defaultIsFailedAuthorization(response) {
  return response.status === 401 && response.config.headers.Authorization && response.config.headers.Authorization.indexOf("Bearer") === 0;
}

function defaultGetAccessFromRefreshResponse(response) {
  return response.data.access_token;
}

var defaultAxiosInstance = _axios2.default.create();

var defaultOptions = {
  // optional. Axios instance used to make refresh request. By default this is an axios instance with no configuration or interceptors.
  axiosInstance: defaultAxiosInstance,
  // optional. Used to take an axios config and add an access token to it. Default behavior is to add an Authorization header with bearer token. You should only need to change this if your API server is using query string instead of header.
  // (axiosConfig, accessToken) -> axiosConfig
  configWithAccess: defaultConfigWithAccess,
  // optional. Used to extract the access token from a refresh response. Default behavior is to select the property "access_token" from the response body. You should only need to change this if your API server did not use the standard name.
  // (axiosResponse) -> string
  getAccessFromRefreshResponse: defaultGetAccessFromRefreshResponse,
  // optional. Used to determine whether a failed request was due to an auth issue. If true, the onSignOut function will be invoked. Default behavior is to check for a 401 and the presence of a bearer token in the Authorization header. You should only need to change this if your API server is using query string instead of header.
  // (axiosResponse) -> bool
  isFailedAuthorization: defaultIsFailedAuthorization,
  // required. Used for interceptor to fetch current auth values before making requests
  // () -> { accessToken, expireDate, refreshToken }
  getAuthData: null,
  // required. Used to create an axios config for a refresh request.
  // (refreshToken) -> axiosConfig
  getRefreshConfig: null,
  // required. Invoked after a successful refresh.
  // (axiosResponse) -> void
  onSignIn: null,
  // required. Invoked after a failed refresh or request which failed due to bad auth.
  // () -> void
  onSignOut: null,
  // optional. Controls whether refresh logic is logged. Default false
  verbose: false
};

function createRequestInterceptor(options) {
  // refreshLock represents whether there is currently an auth refresh happening. While refreshing,
  // refreshLock will be a Promise which resolves to an access token. While idle, it will be null
  var refreshLock = null;
  return function (cfg) {
    var authData = options.getAuthData();

    if (!authData.accessToken) {
      return cfg;
    }

    if (!refreshLock && authData.expireDate <= new Date() && authData.refreshToken) {
      if (options.verbose) {
        console.info("[AUTH REFRESH] Attempting auth refresh");
      }

      refreshLock = options.axiosInstance.request(options.getRefreshConfig(authData.refreshToken)).then(function (response) {
        if (options.verbose) {
          console.info("[AUTH REFRESH] Refresh successful");
        }

        options.onSignIn(response);
        return options.getAccessFromRefreshResponse(response);
      }).catch(function (err) {
        if (options.verbose) {
          console.info("[AUTH REFRESH] Refresh failed");
          console.warn(err);
        }

        options.onSignOut();
        err._isFailedRefresh = true;
        throw err;
      });
    }

    if (refreshLock) {
      if (options.verbose) {
        console.info("[AUTH REFRESH] Waiting on refresh before attempting request");
      }

      var queuedRequest = refreshLock.then(function (newAccessToken) {
        if (!newAccessToken) {
          console.warn("No access token found in refresh response body. This probably means that you need to provide a custom getAccessFromRefreshResponse implementation.");
        }

        refreshLock = null;
        return options.configWithAccess(cfg, newAccessToken);
      });
      queuedRequest.catch(function (err) {
        if (err._isFailedRefresh) {
          refreshLock = null;
        } else if (options.verbose) {
          console.warn(err);
        }
      });
      return queuedRequest;
    }

    return options.configWithAccess(cfg, authData.accessToken);
  };
}

function createFailureInterceptor(options) {
  return function (err) {
    if (err.response && options.isFailedAuthorization(err.response)) {
      options.onSignOut();
    }

    return Promise.reject(err);
  };
}

function createInterceptor(options) {
  var finalOptions = _extends({}, defaultOptions, options);

  return {
    request: createRequestInterceptor(finalOptions),
    failure: createFailureInterceptor(finalOptions)
  };
}